import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { AnimationOptions } from 'ngx-lottie';
import { ConfirmationService, MessageService } from 'primeng/api';
import { Table } from 'primeng/table';
import { TeacherCreateDto, TeacherDto } from 'src/app/aplication/api/Manipulate/CoursesObjects/TeacherDto';
import { TeacherService } from 'src/app/aplication/api/Services/CourseServices/TeacherService.service';
import { UploadService } from 'src/app/aplication/api/Services/UploadService/Upload.service';
import { TEACHER_FORM_VALIDATORS } from 'src/app/aplication/api/Models/Upload.model';

@Component({
  selector: 'app-teachers',
  templateUrl: './teachers.component.html',
  styleUrl: './teachers.component.scss'
})
export class TeachersComponent implements OnInit {
  @ViewChild('dt') table!: Table;

  loaded: boolean = false;
  showFilters: boolean = false;
  uploadingPhoto: boolean = false;
  submitting: boolean = false;

  // Formulário para criação/edição
  teacherForm!: FormGroup;
  isEditing: boolean = false;
  editingTeacher: TeacherDto | null = null;

  profilePhoto: FileUploadDto = new FileUploadDto();

  animationNotFound: AnimationOptions = {
    path: 'assets/Animations/animationNotFoundPages.json',
  };

  animationLoading: AnimationOptions = {
    path: 'assets/Animations/animationLoader.json',
  };

  private _listTeachers: TeacherDto[] = [];
  public get listTeachers(): TeacherDto[] {
    return this._listTeachers;
  }
  @Input() public set listTeachers(v: TeacherDto[]) {
    this._listTeachers = v;
    this.loaded = true;
  }

  constructor(
    protected _teacherService: TeacherService,
    protected messageService: MessageService,
    protected _confirmationService: ConfirmationService,
    private _uploadService: UploadService
  ) {}

  ngOnInit(): void {
    this.initForm();
  }

  private initForm(): void {
    this.teacherForm = new FormGroup({
      name: new FormControl('', [
        Validators.required,
        Validators.minLength(3),
        Validators.maxLength(100)
      ]),
      email: new FormControl('', [
        Validators.required,
        Validators.email
      ]),
      bio: new FormControl('', [
        Validators.maxLength(500)
      ])
    });
  }

  // Validação
  get nameError(): string {
    const control = this.teacherForm.get('name');
    if (!control || !control.errors || !this.teacherForm.touched) return '';
    if (control.errors['required']) return TEACHER_FORM_VALIDATORS.name.required;
    if (control.errors['minlength']) return TEACHER_FORM_VALIDATORS.name.minlength;
    if (control.errors['maxlength']) return TEACHER_FORM_VALIDATORS.name.maxlength;
    return '';
  }

  get emailError(): string {
    const control = this.teacherForm.get('email');
    if (!control || !control.errors || !this.teacherForm.touched) return '';
    if (control.errors['required']) return TEACHER_FORM_VALIDATORS.email.required;
    if (control.errors['email']) return TEACHER_FORM_VALIDATORS.email.pattern;
    return '';
  }

  get bioError(): string {
    const control = this.teacherForm.get('bio');
    if (!control || !control.errors) return '';
    if (control.errors['maxlength']) return TEACHER_FORM_VALIDATORS.bio.maxlength;
    return '';
  }

  get isFormValid(): boolean {
    return this.teacherForm?.valid ?? false;
  }

  // Getter para novo professor
  get newTeacher(): TeacherDto {
    return {
      name: '',
      email: '',
      role: '',
      bio: '',
      profilePhotoUrl: '',
      isActive: true
    };
  }

  openNewTeacherDialog(): void {
    this.editingTeacher = this.newTeacher;
    this.teacherForm.reset();
    this.isEditing = true;
  }

  onImageError(event: Event): void {
    const target = event.target as HTMLImageElement;
    target.src = 'assets/defaultImage.png';
  }

  addRow(): void {
    const newItem = {
      name: '',
      bio: '',
      email: '',
      role: '',
    } as TeacherDto;

    setTimeout(() => {
      this.listTeachers = [...this.listTeachers, newItem];
      this.table.initRowEdit(newItem);
    });
  }

  /**
   * Inicia a edição de um professor
   */
  initEdit(teacher: TeacherDto): void {
    this.editingTeacher = { ...teacher };
    this.teacherForm.patchValue({
      name: teacher.name,
      email: teacher.email,
      bio: teacher.bio || ''
    });
    this.isEditing = true;
  }

  /**
   * Salva as alterações do professor
   */
  saveTeacher(): void {
    if (this.submitting) return;

    this.teacherForm.markAllAsTouched();
    if (!this.teacherForm.valid || !this.editingTeacher) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Atenção',
        detail: 'Preencha todos os campos corretamente',
        life: 3000
      });
      return;
    }

    this.submitting = true;
    const formValue = this.teacherForm.value;

    const updatedTeacher: TeacherDto = {
      ...this.editingTeacher,
      name: formValue.name.trim(),
      email: formValue.email.trim(),
      bio: formValue.bio?.trim() || ''
    };

    if (updatedTeacher.id) {
      this._teacherService.Edit(updatedTeacher).subscribe({
        next: () => {
          this.submitting = false;
          this.messageService.add({
            severity: 'success',
            summary: 'Sucesso',
            detail: 'Professor atualizado com sucesso!',
            life: 2000
          });
          this.cancelEdit();
          // Atualiza a lista local
          const index = this.listTeachers.findIndex(t => t.id === updatedTeacher.id);
          if (index >= 0) {
            this.listTeachers[index] = updatedTeacher;
            this.listTeachers = [...this.listTeachers];
          }
        },
        error: (error) => {
          this.submitting = false;
          this.messageService.add({
            severity: 'error',
            summary: 'Erro',
            detail: this.extractErrorMessage(error),
            life: 4000
          });
        }
      });
    } else {
      // Criar novo professor
      const createDto = new TeacherCreateDto();
      createDto.name = formValue.name.trim();
      createDto.email = formValue.email.trim();
      createDto.bio = formValue.bio?.trim() || '';

      this._teacherService.AddTeacher(createDto).subscribe({
        next: (response) => {
          this.submitting = false;
          updatedTeacher.id = response.id;
          this.messageService.add({
            severity: 'success',
            summary: 'Sucesso',
            detail: 'Professor criado com sucesso!',
            life: 2000
          });
          this.cancelEdit();
          this.listTeachers = [...this.listTeachers, updatedTeacher];
        },
        error: (error) => {
          this.submitting = false;
          this.messageService.add({
            severity: 'error',
            summary: 'Erro',
            detail: this.extractErrorMessage(error),
            life: 4000
          });
        }
      });
    }
  }

  /**
   * Cancela a edição
   */
  cancelEdit(): void {
    this.isEditing = false;
    this.editingTeacher = null;
    this.teacherForm.reset();
  }

  /**
   * Confirma exclusão de professor
   */
  ConfirmDelete(event: any, teacher: TeacherDto, index: number): void {
    if (teacher.id) {
      this._confirmationService.confirm({
        target: event.target as EventTarget,
        header: 'Confirmar exclusão',
        message: `Tem certeza que deseja excluir o professor "${teacher.name}"?`,
        icon: 'pi pi-exclamation-triangle',
        accept: () => {
          this._teacherService.Delete(teacher.id!).subscribe({
            next: () => {
              this.messageService.add({
                severity: 'success',
                summary: 'Sucesso',
                detail: 'Professor excluído com sucesso',
                life: 2000
              });
              this.listTeachers.splice(index, 1);
              this.listTeachers = [...this.listTeachers];
            },
            error: () => {
              this.messageService.add({
                severity: 'error',
                summary: 'Erro',
                detail: 'Erro ao excluir professor'
              });
            }
          });
        }
      });
    } else {
      // Professor ainda não salvo, apenas remove da lista
      this.listTeachers.splice(index, 1);
      this.listTeachers = [...this.listTeachers];
    }
  }

  /**
   * Abre a foto em nova aba
   */
  openPhoto(url: string): void {
    if (url) {
      window.open(url, '_blank');
    }
  }

  /**
   * Upload de foto de perfil usando o serviço centralizado
   */
  onFileSelect(event: any, teacher: TeacherDto): void {
    const selectedFiles: File[] = event.currentFiles;
    if (selectedFiles.length === 0) return;

    const file = selectedFiles[0];

    // Validação
    const validation = this._uploadService.validateFile(file, {
      maxSizeMB: 5,
      allowedTypes: ['image/png', 'image/jpeg', 'image/jpg', 'image/webp']
    });

    if (!validation.valid) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Atenção',
        detail: validation.error,
        life: 4000
      });
      return;
    }

    this.uploadingPhoto = true;
    this.profilePhoto.files = selectedFiles;

    this._uploadService.uploadTeacherProfilePhoto(teacher.id!, file).subscribe({
      next: (result) => {
        this.uploadingPhoto = false;
        this.profilePhoto = new FileUploadDto();
        if (result.success && result.url) {
          teacher.profilePhotoUrl = result.url;
          this.listTeachers = [...this.listTeachers];
          this.messageService.add({
            severity: 'success',
            summary: 'Sucesso',
            detail: 'Foto atualizada com sucesso!',
            life: 2000
          });
        }
      },
      error: (error) => {
        this.profilePhoto = new FileUploadDto();
        this.uploadingPhoto = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Erro',
          detail: this.extractErrorMessage(error)
        });
      }
    });
  }

  /**
   * Extrai mensagem de erro da resposta HTTP
   */
  private extractErrorMessage(error: any): string {
    if (error.error?.message) {
      return error.error.message;
    }
    if (error.error?.errors) {
      const errors = error.error.errors;
      const firstKey = Object.keys(errors)[0];
      if (firstKey && errors[firstKey]) {
        return errors[firstKey][0] || 'Erro de validação';
      }
    }
    return 'Erro na operação. Tente novamente.';
  }
}

export interface FileUploadDto {
  files: File[];
}

export class FileUploadDto {
  files: File[] = [];
}
